/**
 * Data Transformation Utilities for TPRA Risks
 *
 * Transforms API responses into UI-compatible formats by:
 * - Renaming fields (vendor_name → vendor, gap_description → gap, references → sources)
 * - Using API-provided fields (vendor_logo_url, severity_score)
 */

import type { RiskItem, RiskItemAPI, RisksAPIResponse } from '../types/risks';

/**
 * Transforms a single risk record from API format to UI format
 *
 * Field Mappings:
 * - vendor_name → vendor
 * - product_name → product
 * - vendor_logo_url → vendor_icon (uses URL directly)
 * - gap_description → gap
 * - recommendation_description → recommendations
 * - references → sources
 * - severity_score → severity_level (uses API value)
 *
 * @param apiRisk - Raw risk data from API
 * @returns Enhanced risk data with UI-compatible fields
 */
export function transformRiskItem(apiRisk: RiskItemAPI): RiskItem {
  return {
    id: apiRisk.id,
    vendor: apiRisk.vendor_name,
    product: apiRisk.product_name,
    vendor_icon: apiRisk.vendor_logo_url,
    reporting_date: apiRisk.reporting_date,
    control_title: apiRisk.control_title,
    gap: apiRisk.gap_description,
    severity: apiRisk.severity,
    recommendations: apiRisk.recommendation_description,
    sources: apiRisk.references,
    severity_level: apiRisk.severity_score,
  };
}

/**
 * Transforms TPRA Risks API response to UI-compatible format
 *
 * Processes entire API response including:
 * - Transforming each risk record with field mappings
 * - Preserving pagination metadata
 * - Maintaining response structure
 *
 * @param response - Raw API response with risks list
 * @returns Transformed response with UI-compatible risk data
 */
export function transformRisksData(response: RisksAPIResponse): Omit<RisksAPIResponse, 'risks'> & { risks: RiskItem[] } {
  return {
    ...response,
    risks: response.risks.map(transformRiskItem),
  };
}
